/**
 * The adjustment records behind one month's right-first-time figure.
 *
 * Transplanted from the reference implementation. Two changes, both forced by the
 * portal's payload rather than by taste:
 *
 * * Rows arrive keyed through `shared/rows.ts` because the payload is positional
 *   (§8.6), so a cell is read by column key, never by index.
 * * The three source SKU columns are combined into one ordered list **here**, not
 *   in the backend: a response cell is a scalar, so the columns travel raw and are
 *   joined client-side. This is where the reference's `_load` collapse moved to.
 *
 * The three SKU columns are sparse (brief §7a), so they render as one combined
 * cell rather than three mostly-empty columns. Records with no SKU recorded still
 * appear — the adjustment happened whether or not a SKU was captured.
 *
 * `Event ID` is unique per site, not globally (brief §7a), so it is a safe row key
 * here only because these rows are all one site's.
 */

import { useMemo, useState } from 'react'

import { displayText, formatDate } from '../shared/format'
import type { Keyed } from '../shared/rows'
import { count, text } from '../shared/rows'

type SortKey = 'collection_date' | 'event_id' | 'program_control_name'

/** The three sparse SKU columns as one ordered list, blanks dropped. */
function skusOf(row: Keyed): string[] {
  return [text(row.sku_1), text(row.sku_2), text(row.sku_3)].filter(
    (sku): sku is string => sku !== null,
  )
}

export function AdjustmentTable({ rows }: { rows: readonly Keyed[] }) {
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: 'collection_date',
    asc: false,
  })

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[sort.key] ?? ''
      const bv = b[sort.key] ?? ''
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
      return sort.asc ? cmp : -cmp
    })
    return copy
  }, [rows, sort])

  if (rows.length === 0) {
    return <div className="state">No adjustments recorded for this month.</div>
  }

  const header = (key: SortKey, label: string) => (
    <th
      className="sortable"
      onClick={() => {
        setSort({ key, asc: sort.key === key ? !sort.asc : true })
      }}
      aria-sort={sort.key === key ? (sort.asc ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.key === key && (sort.asc ? ' ↑' : ' ↓')}
    </th>
  )

  const missingSkus = rows.filter((r) => skusOf(r).length === 0).length

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table rft-table">
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 300 }} />
            <col style={{ minWidth: 280 }} />
          </colgroup>
          <thead>
            <tr>
              {header('event_id', 'Event ID')}
              {header('collection_date', 'Collected')}
              {header('program_control_name', 'Program control')}
              <th>Adjustment SKUs</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const eventId = count(row.event_id)
              const skus = skusOf(row)
              return (
                <tr key={eventId} className="detail-row">
                  <td className="cell-mono">{eventId}</td>
                  <td className="cell-mono">{formatDate(text(row.collection_date))}</td>
                  <td
                    className="cell-truncate"
                    title={displayText(text(row.program_control_name))}
                  >
                    {displayText(text(row.program_control_name))}
                  </td>
                  <td>
                    {skus.length > 0 ? (
                      <span className="sku-list">
                        {skus.map((sku) => (
                          <span key={sku} className="sku-chip">
                            {sku}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="sku-none">None recorded</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {missingSkus > 0 && (
        <p className="card-subtitle" style={{ marginTop: 10 }}>
          {missingSkus} of {rows.length}{' '}
          {missingSkus === 1 ? 'record has' : 'records have'} no SKU recorded.
        </p>
      )}
    </>
  )
}
